import { Component, OnInit, ViewChild, OnDestroy } from '@angular/core';
import { AbstractControl, UntypedFormControl, UntypedFormGroup, ValidationErrors, Validators } from '@angular/forms';
import { Router } from '@angular/router';

import { Observable, Subject } from 'rxjs';
import { tap, take, takeUntil, debounceTime } from 'rxjs/operators';
import { Store, select } from '@ngrx/store';
import * as DecoupledEditor from '@ckeditor/ckeditor5-build-decoupled-document';
import { TranslateService } from '@ngx-translate/core';

import * as fromRootStore from '@apps/root/store';
import * as fromIts2Store from '@apps/its2/store';
import { IAuroraState } from '@apps/aurora.state';
import { SelectOption } from '@ui/components/form/common/select-option';
import { Its2Constants } from '@apps/its2/its2.constants';
import { CreateRequestViewModel } from '@apps/its2/models/create-request-view-model';
import { Request } from '@apps/its2/domain/models/request';
import { PopinComponentBase } from '@ui/misc/popin-modal/popin-component-base';
import { GuardModalService } from '@ui/misc/guard-modal/guard-modal.service';
import { AppConstants } from '@apps/app.constants';
import { LanguageService } from '@common/services';
import { UploadFilesComponent } from '@customer/itsm/upload-files';
import { TreeModel } from '@ui/components/form/controls/treeview';
import { InputTreeview2Component } from '@ui/components/form/controls/input/input-treeview2/input-treeview2.component';
import { groupRadioButtonValidator } from '@common/validators';
import { CustomFile } from '@common/models/custom-file';
import { PopinModalRef } from '@ui/misc/popin-modal/popin-modal.ref';
import { OpenPopinOriginator } from '@common/enum/open-popin-originator.enum';
import { DashConstants } from '@apps/dash/dash.constants';
import { BckpConstants } from '@apps/bckp/bckp.constants';
import { UploadFilesService } from '@customer/itsm/upload-files/upload-files.service';

@Component({
    selector: 'aa-create-case',
    templateUrl: './create-case.component.html',
    styleUrls: [
        '../request-create.scss',
        './create-case.component.scss'
    ],
    standalone: false
})
export class CreateCaseComponent extends PopinComponentBase implements OnInit, OnDestroy
{
    @ViewChild('filesToUpload', { static: true }) filesToUpload: UploadFilesComponent;
    @ViewChild('inputTreeview', { static: true }) inputTreeview: InputTreeview2Component;
    private destroyator = new Subject();
    public isShown: boolean = false;
    public formGroup: UntypedFormGroup;
    public catalog: Observable<TreeModel>;
    public urgencyOptions: SelectOption[];
    public editor = DecoupledEditor;
    public configEditor = AppConstants.configEditor;
    public submitEnabled: boolean = true;
    public request: Request;
    public readonly titleField: string = 'title';
    public readonly descriptionField: string = 'description';
    public readonly urgencyIdField: string = 'urgencyId';
    public readonly catalogField: string = 'catalogGuid';
    public readonly fileToUploadField: string = 'fileUpload';

    constructor (
        guardModalService: GuardModalService,

        popinModalRef: PopinModalRef,
        private router: Router,
        private store: Store<IAuroraState>,
        private translateService: TranslateService,
        private languageService: LanguageService,
        private uploadFilesService: UploadFilesService
    )
    {
        super(guardModalService);

        try
        {
            this.openPopinOriginator = parseInt(popinModalRef.data[AppConstants.OpenPopinOriginator], 10);

            if (this.openPopinOriginator === OpenPopinOriginator.NO_ROUTE)
            {
               
		
				this.languageService.loadLanguage('itsm')
                    .pipe(
                        tap(() => 
                        {
                            const lang = this.languageService.getShortItsmLanguage();
                    
                            this.store.dispatch(fromIts2Store.Its2ValuesActions.Its2RequestsValuesRequested({
                                propertyName: `urgency${lang}`
                            }));
                        }),
                        take(1)
                    ).subscribe();
            }
        }
        catch (err)
        {
            this.openPopinOriginator = OpenPopinOriginator.ITS2_REQUESTS;
        }

        //TODO - Vu avec Fred en attendant de voir quelle stratégie adopter
        this.urgencyOptions = Its2Constants.getUrgencyOptions(this.translateService);
        this.catalog = this.store.select(fromIts2Store.getIts2CatalogsIncident, this.languageService.getShortItsmLanguage());

        this.createForm();
    }

    ngOnInit ()
    {
        Promise.resolve(null).then(() => this.isShown = true);
    }

    ngOnDestroy (): void
    {
        this.destroyator.next(null);
        this.destroyator.complete();
    }

    onCreate = () =>
    {
        if (this.formGroup.invalid)
        {
            this.markControlsDirty(this.formGroup);

            return;
        }

        this.submitEnabled = false;

        const crvm: CreateRequestViewModel = {
            catalogGuid: this.formGroup.get(this.catalogField).value.catalogGuid,
            urgencyId: this.formGroup.get(this.urgencyIdField).value,
            title: this.formGroup.get(this.titleField).value,
            description: this.formGroup.get(this.descriptionField).value
        };

        this.store.dispatch(
            fromIts2Store.Its2RequestsActions.Its2RequestsCreateCaseRequested({ createCase: crvm })
        );
        this.close();
    };

    onClosePopin = (): void =>
    {
        this.store
            .pipe(
                select(fromRootStore.getPopinFormWithUploadFileDirtyStatus),
                take(1),
                tap((status: boolean) =>
                {
                    this.preventClosePopinForm(null, status)
                        .pipe(
                            take(1),
                            tap((dialogResult: boolean) =>
                            {
                                if (dialogResult)
                                {
                                    this.close();
                                    this.uploadFilesService.clearFiles();
                                }
                            })
                        )
                        .subscribe();
                })
            )
            .subscribe();
    };

    onFilesChange (evt: CustomFile[]): void
    {
        this.store.dispatch(
            fromRootStore.RootMiscStoreActions.RootDirtyUploadFileSet({ dirty: evt.length > 0 })
        );
    }

    noHtmlValidator (control: AbstractControl) : ValidationErrors | null {
        const htmlTagRegex = /<[^>]*>/;
        return htmlTagRegex.test(control.value) ? { htmlNotAllowed: true } : null;
    }

    private createForm ()
    {
        this.formGroup = new UntypedFormGroup({
            [this.titleField]: new UntypedFormControl('', [Validators.required, this.noHtmlValidator]),
            [this.descriptionField]: new UntypedFormControl('', Validators.required),
            [this.urgencyIdField]: new UntypedFormControl('', groupRadioButtonValidator),
            [this.catalogField]: new UntypedFormControl('', Validators.required),
            [this.fileToUploadField]: new UntypedFormControl('')
        });

        this.formGroup.valueChanges
            .pipe(
                debounceTime(250),
                tap(() =>
                {
                    this.store.dispatch(
                        fromRootStore.RootMiscStoreActions.RootDirtyPopinFormSet({ dirty: true })
                    );
                }),
                takeUntil(this.destroyator)
            )
            .subscribe();
    }

    private close ()
    {
        this.inputTreeview.destroy();
        this.store.dispatch(
            fromRootStore.RootMiscStoreActions.RootDirtyPopinFormSet({ dirty: false })
        );
        this.store.dispatch(
            fromRootStore.RootMiscStoreActions.RootDirtyUploadFileSet({ dirty: false })
        );
        this.isShown = false;

        if (this.openPopinOriginator !== OpenPopinOriginator.NO_ROUTE)
        {
            switch (this.openPopinOriginator)
            {
                case OpenPopinOriginator.DASH_REQUESTS:
                    this.router.navigate(DashConstants.MainRoute());

                    break;

                case OpenPopinOriginator.BCKP_DASHBOARD:
                    this.router.navigate(BckpConstants.DashboardRoute());

                    break;

                default:
                    this.router.navigate([`${Its2Constants.BaseUrl}/${Its2Constants.Requests}`]);
            }
        }
    }
}
